package com.gsb.vyapar.bhagya.presentation.common.image

import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.rememberTransformableState
import androidx.compose.foundation.gestures.transformable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.animation.Crossfade
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.graphics.painter.BitmapPainter
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.gsb.vyapar.bhagya.interactor.event.UiEvents
import com.gsb.vyapar.bhagya.interactor.uistate.UiState
import com.gsb.vyapar.bhagya.theme.getImageForCategory
import com.gsb.vyapar.bhagya.theme.placeHolder
import com.gsb.vyapar.bhagya.theme.splash
import com.gsb.vyapar.bhagya.util.PLACEHOLDERIMG
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource

@Composable
fun CommonImage(painter: Painter, modifier: Modifier = Modifier) {
    Image(painter, contentDescription = "GVB", modifier = modifier)
}

@Composable
fun CommonImageRound(painter: Painter, modifier: Modifier = Modifier) {
    Image(painter, contentDescription = "GVB", modifier = modifier.clip(CircleShape))
}

@Composable
fun SplashImage() {
    CommonImage(painterResource(splash))
}

@Composable
fun SplashImageCircular() {
    CommonImageRound(painterResource(splash))
}

@Composable
fun SplashImageCircularAnimated() {
    val infiniteTransition = rememberInfiniteTransition(label = "splashRotation")
    val rotationY by infiniteTransition.animateFloat(
        initialValue = -15f,
        targetValue = 15f,
        animationSpec = infiniteRepeatable(
            animation = tween(durationMillis = 2000, easing = LinearEasing),
            repeatMode = RepeatMode.Reverse
        ),
        label = "rotationY"
    )
    val rotationZ by infiniteTransition.animateFloat(
        initialValue = -5f,
        targetValue = 5f,
        animationSpec = infiniteRepeatable(
            animation = tween(durationMillis = 2000, easing = LinearEasing),
            repeatMode = RepeatMode.Reverse
        ),
        label = "rotationZ"
    )
    val scale by infiniteTransition.animateFloat(
        initialValue = 0.85f,
        targetValue = 1.15f,
        animationSpec = infiniteRepeatable(
            animation = tween(durationMillis = 1500, easing = LinearEasing),
            repeatMode = RepeatMode.Reverse
        ),
        label = "scale"
    )
    val density = LocalDensity.current
    CommonImageRound(
        painterResource(splash),
        modifier = Modifier.graphicsLayer {
            this.rotationY = rotationY
            this.rotationZ = rotationZ
            scaleX = scale
            scaleY = scale
            cameraDistance = 8 * density.density
        }
    )
}

@Composable
fun CommonImage(imageBitmap: ImageBitmap, width: Dp = 100.dp, height: Dp = 100.dp, modifier: Modifier = Modifier) {
    Image(
        painter = BitmapPainter(imageBitmap),
        contentDescription = "image",
        modifier = modifier.width(width).height(height),
    )
}

@Composable
fun CommonImage(drawableResource: DrawableResource, width: Dp = 100.dp, height: Dp = 100.dp, modifier: Modifier = Modifier) {
    Image(
        painter = painterResource(drawableResource),
        contentDescription = "image",
        modifier = modifier.width(width).height(height),
    )
}

@Composable
fun CommonImageRound(imageBitmap: ImageBitmap, width: Dp = 100.dp, height: Dp = 100.dp, modifier: Modifier = Modifier) {
    Image(
        painter = BitmapPainter(imageBitmap),
        contentDescription = "image",
        modifier = modifier.width(width).height(height).clip(CircleShape),
    )
}

@Composable
fun CommonImageRound(drawableResource: DrawableResource, width: Dp = 100.dp, height: Dp = 100.dp, modifier: Modifier = Modifier) {
    Image(
        painter = painterResource(drawableResource),
        contentDescription = "image",
        modifier = modifier.width(width).height(height).clip(CircleShape),
    )
}

@Composable
fun PlaceHolder(width: Dp = 100.dp, height: Dp = 100.dp, modifier: Modifier = Modifier) {
    Image(
        painter = painterResource(placeHolder),
        contentDescription = "placeHolder",
        modifier = modifier.width(width).height(height),
    )
}

@Composable
fun PlaceHolder(drawableResource: DrawableResource, width: Dp = 100.dp, height: Dp = 100.dp, modifier: Modifier = Modifier) {
    Image(
        painter = painterResource(drawableResource),
        contentDescription = "placeHolder",
        modifier = modifier.width(width).height(height),
    )
}

@Composable
fun SocialPlaceHolder(drawableResource: DrawableResource, width: Dp = 35.dp, height: Dp = 35.dp, modifier: Modifier = Modifier) {
    Image(
        painter = painterResource(drawableResource),
        contentDescription = "placeHolder",
        modifier = modifier.width(width).height(height),
    )
}

@Composable
fun StorePlaceHolder(drawableResource: DrawableResource, width: Dp = 85.dp, height: Dp = 60.dp, modifier: Modifier = Modifier) {
    Image(
        painter = painterResource(drawableResource),
        contentDescription = "placeHolder",
        modifier = modifier.width(width).height(height),
    )
}

@Composable
fun CategoryPlaceHolder(id: Int, width: Dp = 50.dp, height: Dp = 50.dp) {
    Image(
        painter = painterResource(getImageForCategory(id)),
        contentDescription = "CategoryPlaceHolder",
        modifier = Modifier.width(width).height(height),
    )
}

@Composable
fun BusinessCardPlaceHolder(drawableResource: DrawableResource, width: Dp = 30.dp, height: Dp = 30.dp, modifier: Modifier = Modifier) {
    Image(
        painter = painterResource(drawableResource),
        contentDescription = "placeHolder",
        modifier = modifier.width(width).height(height),
    )
}

@Composable
fun DetailFullImage(imageBitmap: ImageBitmap, modifier: Modifier = Modifier) {
    Image(
        painter = BitmapPainter(imageBitmap),
        contentDescription = "image",
        contentScale = ContentScale.Fit,
        modifier = modifier,
    )
}

@Composable
fun ZoomableImage(imageBitmap: ImageBitmap, modifier: Modifier = Modifier) {
    var scale by remember { mutableStateOf(1f) }
    var offsetX by remember { mutableStateOf(0f) }
    var offsetY by remember { mutableStateOf(0f) }

    val transformableState = rememberTransformableState { zoomChange, panChange, _ ->
        scale = (scale * zoomChange).coerceIn(1f, 4f)
        offsetX += panChange.x
        offsetY += panChange.y
    }

    Box(
        modifier = modifier
            .fillMaxSize()
            .background(Color.Black)
            .transformable(transformableState)
    ) {
        DetailFullImage(
            imageBitmap,
            modifier = Modifier
                .fillMaxSize()
                .graphicsLayer(
                    scaleX = scale,
                    scaleY = scale,
                    translationX = offsetX,
                    translationY = offsetY
                )
        )
    }
}

@Composable
fun SmoothImage(
    imageBitmap: ImageBitmap?,
    width: Dp = 100.dp,
    height: Dp = 100.dp,
    modifier: Modifier = Modifier,
    placeholder: DrawableResource = placeHolder
) {
    Crossfade(targetState = imageBitmap, animationSpec = tween(durationMillis = 300)) { bitmap ->
        if (bitmap != null) {
            Image(
                painter = BitmapPainter(bitmap),
                contentDescription = "image",
                modifier = modifier.width(width).height(height)
            )
        } else {
            Image(
                painter = painterResource(placeholder),
                contentDescription = "placeHolder",
                modifier = modifier.width(width).height(height)
            )
        }
    }
}

@Composable
fun SmoothImageRound(
    imageBitmap: ImageBitmap?,
    width: Dp = 100.dp,
    height: Dp = 100.dp,
    modifier: Modifier = Modifier,
    placeholder: DrawableResource = placeHolder,
    contentScale: ContentScale = ContentScale.Crop,
) {
    Crossfade(targetState = imageBitmap, animationSpec = tween(durationMillis = 300)) { bitmap ->
        if (bitmap != null) {
            Image(
                painter = BitmapPainter(bitmap),
                contentDescription = "image",
                modifier = modifier.width(width).height(height).clip(CircleShape),
                contentScale = contentScale
            )
        } else {
            Image(
                painter = painterResource(placeholder),
                contentDescription = "placeHolder",
                modifier = modifier.width(width).height(height).clip(CircleShape),
                contentScale = contentScale
            )
        }
    }
}
